import { generateHeroImageUrl } from '@otovo/shared/lib/sanity/imageUtils';
import {
  Sanity$Image,
  Sanity$ImageHotspot,
} from '@otovo/shared/types/sanityImage';
import { stegaClean } from 'next-sanity';

function getObjectPosition(hotspot: Sanity$ImageHotspot) {
  return `${(hotspot.x * 100).toFixed(2)}% ${(hotspot.y * 100).toFixed(2)}%`;
}

const RESOLUTIONS = [2560, 1920, 1600, 1280, 960, 640];

type Props = {
  image: Sanity$Image;
};

type UrlConfig = {
  urls: [string, string];
  width: number;
  orientation: 'landscape' | 'portrait';
  isSmallestVariant: boolean;
};

function getUrlConfig(image: Sanity$Image): UrlConfig[] {
  return RESOLUTIONS.flatMap((size) => {
    const portraitWidth = Math.round(size / (16 / 9));
    const landscapeHeight = portraitWidth;
    const isSmallestVariant = size === RESOLUTIONS[RESOLUTIONS.length - 1];

    return [
      {
        urls: [
          generateHeroImageUrl(image, size, landscapeHeight, 1),
          generateHeroImageUrl(image, size, landscapeHeight, 2),
        ],
        width: size,
        orientation: 'landscape',
        isSmallestVariant,
      },
      {
        dpr: 1,
        urls: [
          generateHeroImageUrl(image, portraitWidth, size, 1),
          generateHeroImageUrl(image, portraitWidth, size, 2),
        ],
        width: portraitWidth,
        orientation: 'portrait',
        isSmallestVariant,
      },
    ];
  });
}

const HeroImage = ({ image }: Props) => {
  const heroImageUrlConfigs = getUrlConfig(image);

  const altText = stegaClean(image?.alt);

  return (
    <picture>
      {heroImageUrlConfigs.map((heroImageUrlConfig) => {
        let mediaQuery = `(orientation: ${heroImageUrlConfig.orientation})`;
        if (!heroImageUrlConfig.isSmallestVariant) {
          mediaQuery += ` and (width >= ${heroImageUrlConfig.width}px)`;
        }

        return (
          <source
            key={heroImageUrlConfig.urls[0]}
            srcSet={`${heroImageUrlConfig.urls[0]} 1x, ${heroImageUrlConfig.urls[1]} 2x`}
            media={mediaQuery}
          />
        );
      })}
      <img
        src={image.asset.metadata.lqip}
        className="absolute top-0 right-0 bottom-0 left-0 h-full max-h-full w-full object-cover"
        // fetchpriority is valid for imgs, but ESLint and TypeScript isn't up-to-date.
        // @ts-expect-error 2322
        // eslint-disable-next-line
        fetchpriority="high"
        style={{
          objectPosition: image.hotspot
            ? getObjectPosition(image.hotspot)
            : 'left',
        }}
        alt={altText}
      />
    </picture>
  );
};

export default HeroImage;
